<template>
  <div v-if="hasCriticalError" class="error-page">
    <div class="error-page-content">
      <div class="error-page-icon"><AppIcon name="warning" :size="48" /></div>
      <h1 class="error-page-title">Something went wrong</h1>
      <p class="error-page-text">
        An unexpected error occurred. Please reload the page to continue.
      </p>
      <button class="error-page-button" @click="reloadPage">Reload</button>
    </div>
  </div>
  <template v-else>
    <Transition name="offline-banner">
      <div v-if="isOffline" class="offline-banner" role="alert">
        <span class="offline-banner-icon"><AppIcon name="warning" :size="16" /></span>
        You are offline. Some features may not work.
      </div>
    </Transition>
    <router-view />
    <Toast />
  </template>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, onErrorCaptured } from 'vue'
import AppIcon from '@/components/AppIcon.vue'
import Toast from '@/components/Toast.vue'
import { useToastStore } from '@/stores/toast'
import { useLocaleStore } from '@/stores/locale'

const toastStore = useToastStore()
// Initialize locale store — applies dir attribute to <html> on creation
useLocaleStore()

const hasCriticalError = ref(false)
const isOffline = ref(!navigator.onLine)

onErrorCaptured((err: unknown, _instance, info) => {
  if (import.meta.env.DEV) {
    console.error('[Vue Error]', err, '\nInfo:', info)
  }

  window.Sentry?.captureException(err, { info })

  const isRenderError =
    info === 'render function' ||
    info === 'setup function' ||
    info === 'component mounted hook' ||
    info?.startsWith?.('mounted')

  if (isRenderError) {
    hasCriticalError.value = true
  } else {
    const message =
      err instanceof Error ? err.message : 'An unexpected error occurred'
    toastStore.error(message)
  }

  return false
})

function handleUnhandledRejection(event: PromiseRejectionEvent): void {
  const err = event.reason

  if (import.meta.env.DEV) {
    console.error('[Unhandled Promise Rejection]', err)
  }

  window.Sentry?.captureException(err instanceof Error ? err : new Error(String(err)))

  const message =
    err instanceof Error ? err.message : 'An unexpected error occurred'
  toastStore.error(message)
}

function reloadPage(): void {
  window.location.reload()
}

function handleOnline(): void {
  isOffline.value = false
}

function handleOffline(): void {
  isOffline.value = true
}

onMounted(() => {
  window.addEventListener('online', handleOnline)
  window.addEventListener('offline', handleOffline)
  window.addEventListener('unhandledrejection', handleUnhandledRejection)
})

onUnmounted(() => {
  window.removeEventListener('online', handleOnline)
  window.removeEventListener('offline', handleOffline)
  window.removeEventListener('unhandledrejection', handleUnhandledRejection)
})
</script>

<style scoped>
.error-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-primary, #0f1923);
  color: var(--text-primary, #fff);
  font-family: var(--font-family-primary, 'Inter', sans-serif);
  padding: 24px;
}

.error-page-content {
  text-align: center;
  max-width: 420px;
}

.error-page-icon {
  font-size: 48px;
  color: var(--color-warning, #ffa726);
  margin-bottom: 16px;
}

.error-page-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 12px;
}

.error-page-text {
  font-size: 0.9375rem;
  color: var(--text-secondary, #b1bad3);
  line-height: 1.5;
  margin: 0 0 24px;
}

.error-page-button {
  display: inline-block;
  padding: 12px 32px;
  background: var(--accent-primary, #00e701);
  color: var(--bg-primary, #0f1923);
  font-size: 0.9375rem;
  font-weight: 600;
  border: none;
  border-radius: var(--radius-md, 8px);
  cursor: pointer;
  transition: opacity var(--transition-fast, 150ms ease);
}

.error-page-button:hover {
  opacity: 0.9;
}

.offline-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: var(--z-notification, 500);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  background: var(--color-warning, #ffa726);
  color: var(--bg-primary, #0f1923);
  font-size: 0.875rem;
  font-weight: 500;
  font-family: var(--font-family-primary, 'Inter', sans-serif);
}

.offline-banner-icon {
  font-size: 16px;
}

.offline-banner-enter-active,
.offline-banner-leave-active {
  transition: transform 250ms ease, opacity 250ms ease;
}

.offline-banner-enter-from,
.offline-banner-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
</style>
